import { useTranslation } from "react-i18next";
import type { InflectionCell, InflectionTableContent } from "../content-types";
import { cellMatches } from "../grading";
import { Explanation, Prompt } from "./shared";
import { GlossableText, isLanguageFramework } from "./glossable-text";
import type { QuestionRendererProps } from "./types";

/** Conjugation/declension grid. One row per cell: a muted `label` on the left
 *  and a typed input on the right. Graded when every cell's entry matches its
 *  `answer` (diacritic-folded, Levenshtein ≤ 1). */
export function InflectionTableRenderer({
  content,
  answer,
  setAnswer,
  revealed,
  frameworkSlug,
}: QuestionRendererProps) {
  const { t } = useTranslation();
  const c = content as unknown as InflectionTableContent;
  const enableGloss = isLanguageFramework(frameworkSlug);
  const cells: InflectionCell[] = c.cells ?? [];
  const map = (answer as Record<string, string>) ?? {};

  const setFor = (cellId: string, val: string) => setAnswer({ ...map, [cellId]: val });

  return (
    <div>
      <Prompt>
        {enableGloss ? <GlossableText text={c.prompt} context={c.prompt} /> : c.prompt}
      </Prompt>
      {c.table_title && (
        <h3 className="mt-3 text-sm font-semibold text-ink-faint">
          {enableGloss ? (
            <GlossableText text={c.table_title} context={c.prompt} />
          ) : (
            c.table_title
          )}
        </h3>
      )}
      <div className="mt-4 space-y-2.5">
        {cells.map((cell) => {
          const entry = map[cell.cell_id] ?? "";
          const ok = revealed ? cellMatches(cell.answer, entry) : null;
          return (
            <div key={cell.cell_id} className="flex items-center gap-3">
              <span className="w-28 shrink-0 text-sm text-ink-faint">{cell.label}</span>
              <div className="flex-1">
                <input
                  type="text"
                  value={entry}
                  disabled={revealed}
                  onChange={(e) => setFor(cell.cell_id, e.target.value)}
                  className={`w-full rounded-xl border px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 ${
                    revealed
                      ? ok
                        ? "border-emerald-400 bg-emerald-50 text-emerald-700"
                        : "border-red-400 bg-red-50 text-red-700"
                      : "border-black/10 bg-white"
                  }`}
                />
                {revealed && !ok && (
                  <p className="mt-1 text-xs text-emerald-700">
                    {t("Correct:")} <span className="font-semibold">{cell.answer}</span>
                  </p>
                )}
                {cell.hint && (
                  <p className="mt-0.5 text-xs text-ink-faint">{cell.hint}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {revealed && <Explanation text={c.explanation} />}
    </div>
  );
}
